import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useAuth } from "./AuthContext";
import { acceptInvite, inspectInvite } from "./api";
import {
  PENDING_INVITE_TOKEN_KEY,
  buildAuthRedirectPath,
  clearPendingInviteToken,
  normalizeInviteEmail,
  storePendingInviteToken,
} from "./inviteFlow";
import "./InviteAccept.css";

export { PENDING_INVITE_TOKEN_KEY } from "./inviteFlow";

function readTokenFromSearch(search) {
  const params = new URLSearchParams(search);
  return params.get("token")?.trim() || "";
}

function inviteInspectError(data) {
  if (data?.reason === "expired") {
    return "This invitation has expired.";
  }
  if (data?.reason === "revoked") {
    return "This invitation was revoked.";
  }
  if (data?.reason === "accepted") {
    return "";
  }
  return "This invitation link is invalid.";
}

function getInviteErrorCode(err) {
  return err?.originalError?.response?.data?.code || err?.details?.code || null;
}

export default function InviteAccept() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    refreshOrganizationContext,
    selectWorkspace,
  } = useAuth();

  const urlToken = useMemo(
    () => readTokenFromSearch(location.search),
    [location.search]
  );

  const [storedToken, setStoredToken] = useState(
    () => sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY) || ""
  );
  const token = urlToken || storedToken;

  useEffect(() => {
    if (urlToken) {
      storePendingInviteToken(urlToken);
      setStoredToken(urlToken);
    }
  }, [urlToken]);

  const [inspectState, setInspectState] = useState({
    loading: true,
    data: null,
    error: "",
  });
  const [acceptState, setAcceptState] = useState({
    loading: false,
    success: false,
    error: "",
  });

  useEffect(() => {
    let cancelled = false;

    const loadInvite = async () => {
      if (!token) {
        setInspectState({
          loading: false,
          data: null,
          error: "This invitation link is missing or invalid.",
        });
        return;
      }

      setInspectState({ loading: true, data: null, error: "" });
      try {
        const data = await inspectInvite(token);
        if (cancelled) return;
        if (!data?.valid) {
          const message = inviteInspectError(data);
          setInspectState({
            loading: false,
            data,
            error: message,
          });
          return;
        }
        setInspectState({ loading: false, data, error: "" });
      } catch (err) {
        if (cancelled) return;
        setInspectState({
          loading: false,
          data: null,
          error: err?.message || "Could not load invitation details.",
        });
      }
    };

    loadInvite();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const activateInvitedWorkspace = useCallback(async (organizationId) => {
    if (!organizationId) {
      throw new Error("Could not determine the invited workspace.");
    }
    await selectWorkspace(organizationId);
    clearPendingInviteToken();
  }, [selectWorkspace]);

  useEffect(() => {
    if (authLoading || !user || !token || inspectState.data?.valid) {
      return;
    }
    if (inspectState.data?.reason !== "accepted" || !inspectState.data?.organizationId) {
      return;
    }
    if (acceptState.loading || acceptState.success) {
      return;
    }

    let cancelled = false;

    const completeExistingMembership = async () => {
      setAcceptState({ loading: true, success: false, error: "" });
      try {
        if (
          normalizeInviteEmail(user.email) !==
          normalizeInviteEmail(inspectState.data.invitedEmail)
        ) {
          throw new Error(
            "This invitation was sent to a different email address."
          );
        }

        await refreshOrganizationContext();
        await activateInvitedWorkspace(inspectState.data.organizationId);
        if (cancelled) return;
        setAcceptState({ loading: false, success: true, error: "" });
        navigate("/", { replace: true });
      } catch (err) {
        if (cancelled) return;
        setAcceptState({
          loading: false,
          success: false,
          error: err?.message || "Could not open the invited workspace.",
        });
      }
    };

    completeExistingMembership();
    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    user,
    token,
    inspectState.data,
    acceptState.loading,
    acceptState.success,
    refreshOrganizationContext,
    selectWorkspace,
    navigate,
    activateInvitedWorkspace,
  ]);

  useEffect(() => {
    if (authLoading || !user || !token || !inspectState.data?.valid) {
      return;
    }
    if (acceptState.loading || acceptState.success) {
      return;
    }

    let cancelled = false;

    const completeAccept = async () => {
      setAcceptState({ loading: true, success: false, error: "" });
      try {
        const result = await acceptInvite(token);
        if (cancelled) return;

        const organizationId = result?.activeOrganization?.organizationId;
        if (organizationId) {
          await activateInvitedWorkspace(organizationId);
        } else {
          await refreshOrganizationContext();
          clearPendingInviteToken();
        }

        setAcceptState({ loading: false, success: true, error: "" });
        navigate("/", { replace: true });
      } catch (err) {
        if (cancelled) return;

        const errorCode = getInviteErrorCode(err);
        if (errorCode === "ACCEPTED") {
          try {
            const data = await inspectInvite(token);
            if (data?.organizationId) {
              if (
                normalizeInviteEmail(user.email) !==
                normalizeInviteEmail(data.invitedEmail)
              ) {
                throw new Error(
                  "This invitation was sent to a different email address."
                );
              }
              await activateInvitedWorkspace(data.organizationId);
              setAcceptState({ loading: false, success: true, error: "" });
              navigate("/", { replace: true });
              return;
            }
          } catch (switchErr) {
            setAcceptState({
              loading: false,
              success: false,
              error:
                switchErr?.message || "Could not open the invited workspace.",
            });
            return;
          }
        }

        if (errorCode === "EMAIL_MISMATCH") {
          setAcceptState({
            loading: false,
            success: false,
            error: "This invitation was sent to a different email address.",
          });
          return;
        }

        setAcceptState({
          loading: false,
          success: false,
          error: err?.message || "Could not accept invitation.",
        });
      }
    };

    completeAccept();
    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    user,
    token,
    inspectState.data,
    acceptState.loading,
    acceptState.success,
    refreshOrganizationContext,
    selectWorkspace,
    navigate,
    activateInvitedWorkspace,
  ]);

  const loginHref = buildAuthRedirectPath(token, "/login");
  const registerHref = buildAuthRedirectPath(token, "/register");

  const inviteData = inspectState.data;
  const showInviteDetails =
    inviteData &&
    (inviteData.valid || inviteData.reason === "accepted") &&
    !inspectState.error;

  return (
    <div className="invite-accept-container">
      <div className="invite-accept-card">
        <h1>Workspace invitation</h1>

        {inspectState.loading || authLoading ? (
          <p className="invite-accept-muted">Loading invitation…</p>
        ) : inspectState.error ? (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{inspectState.error}</span>
          </div>
        ) : showInviteDetails ? (
          <>
            <p>
              You&apos;ve been invited to join{" "}
              <strong>{inviteData.workspaceName}</strong> as{" "}
              <strong>{inviteData.roleLabel}</strong>.
            </p>
            <p className="invite-accept-muted">
              Invitation for {inviteData.invitedEmail}
            </p>
            {inviteData.reason === "accepted" ? (
              <p className="invite-accept-muted">
                This invitation was already accepted.
                {user
                  ? " Opening the workspace…"
                  : " Sign in to open the workspace."}
              </p>
            ) : null}

            {!user ? (
              <div className="invite-accept-actions">
                <p>Sign in or create an account with the invited email to continue.</p>
                <Link to={loginHref} className="primary-button invite-accept-button">
                  Log in
                </Link>
                <Link to={registerHref} className="secondary-button invite-accept-button">
                  Register
                </Link>
              </div>
            ) : acceptState.loading ? (
              <p className="invite-accept-muted">
                {inviteData.reason === "accepted"
                  ? "Opening workspace…"
                  : "Accepting invitation…"}
              </p>
            ) : acceptState.success ? (
              <div className="invite-accept-success">
                <CheckCircle2 size={18} aria-hidden="true" />
                <span>
                  {inviteData.reason === "accepted"
                    ? "Workspace opened. Redirecting…"
                    : "Invitation accepted. Redirecting…"}
                </span>
              </div>
            ) : acceptState.error ? (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{acceptState.error}</span>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
